import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setUnauthorizedHandler, setAccessToken } from "./api";
import { registerPush } from "./push";
import { toast } from "sonner";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      registerPush();
    } catch {
      setUser(null);
      // Stale bearer token — clear it so future probes don't keep sending it.
      setAccessToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Install the global 401 handler — clears user state + redirects to login with a clear toast.
    setUnauthorizedHandler(() => {
      setUser(null);
      setAccessToken(null);
      try { toast.error("Your session expired. Please sign in again."); } catch {}
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/")) {
        // already on root login route — no-op
      }
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.access_token) setAccessToken(data.access_token);
      setUser(data.user);
      registerPush();
      return { ok: true, user: data.user };
    } catch (e) {
      // Distinguish credential rejection from transport / CORS / network errors so
      // users with the correct password aren't told they got it wrong.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (detail) return { ok: false, error: formatApiError(detail, "Login failed") };
      if (status === 0 || !status) {
        return { ok: false, error: "Could not reach the server. Check your connection and try again." };
      }
      if (status >= 500) {
        return { ok: false, error: "Server error. Please try again in a moment." };
      }
      return { ok: false, error: `Login failed (${status})` };
    }
  };

  const register = async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      if (data?.access_token) setAccessToken(data.access_token);
      setUser(data.user);
      registerPush();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail, "Registration failed") };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setAccessToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
